
import Vue from 'vue';
import _truncate from 'lodash/truncate';
import _isNil from 'lodash/isNil';
import _omit from 'lodash/omit';
import { SETTING_ACTIONS } from '@/views/Runnits/constants';
import { RunnitNodeField, RunnitMask, RUNNIT_NODE_FIELD_TYPE, RUNNIT_NODE_STATIC_FIELDS_KEY, RunnitNodeRunResult } from '@run-diffusion/shared';
import ImageInfoFields from '@/components/ImageGallery/ImageInfoFields.vue';
import { mapActions, mapState } from 'vuex';
import { db, FieldValue, functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'ImageInfo',
	props: {
		nodeRun: { type: Object, required: true },
		insideDrawer: { type: Boolean, default: false },
		insideNode: { type: Boolean, default: false },
		insideCarousel: { type: Boolean, default: false },
		ignoreTruncation: { type: Boolean, default: false },
		index: { type: [String, Number], default: null },
		imageAttributionStyles: { type: Object, default: null },
		canBeFeatured: { type: Boolean, default: false },
	},
	data () {
		return {
			SETTING_ACTIONS,
			RUNNIT_NODE_FIELD_TYPE,

			showMask: true as boolean,
			settingFeaturedState: false as boolean,
			featuredMenu: false as boolean,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		numImgs () {
			return this._get(this.nodeRun, 'results.length') || this._get(this.nodeRun, `staticInputs[${RUNNIT_NODE_STATIC_FIELDS_KEY.numResults}]`) || 1;
		},
		isFirstIndex () {
			return +this.index === 0;
		},
		fieldValueTruncateCharCount () {
			let charCount: number = 400;
			if (this.$vuetify.breakpoint.xsOnly || this.insideDrawer) {
				charCount = 100;
			}
			if (this.ignoreTruncation) {
				charCount = 3000;
			}
			return charCount;
		},
		firstLetterOfEmail () {
			return this.nodeRun.email ? this.nodeRun.email[0].toUpperCase() : '';
		},
		hasFeaturedAccess () {
			const hasFeatureFlag = !!(this.user &&
				this.user.features &&
				this.user.features.featureRunnitResult &&
				this.nodeRun.userId === this.user.id
			);
			const hasAdminAccess = !!(this.user && this.user.isAdmin);
			return hasAdminAccess || hasFeatureFlag;
		},
		initAtTimestamp () {
			const dateValue = this.nodeRun.initAt || this.nodeRun.doneAt || new Date();
			// Handle Firestore Timestamp
			if (dateValue && typeof dateValue.toMillis === 'function') {
				return dateValue.toMillis();
			}
			// Handle Date object
			if (dateValue instanceof Date) {
				return dateValue.getTime();
			}
			// Handle timestamp number or string
			return typeof dateValue === 'number' ? dateValue : new Date(dateValue).getTime();
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar'
		]),
		_truncate,
		_isNil,
		determineWidth () {
			let width = '100%';
			if (
				this.$vuetify.breakpoint.smAndUp &&
				!this.insideDrawer
			) {
				width = '50%';
			}
			return width;
		},
		getInpaintingMask (field: RunnitNodeField): RunnitMask {
			return (
				field &&
				field.type === RUNNIT_NODE_FIELD_TYPE.IMG &&
				this.nodeRun.inputs[field.fieldDefUuid] &&
				this.nodeRun.inputs[field.fieldDefUuid].inpainting &&
				this.nodeRun.inputs[field.fieldDefUuid].inpainting.mask
			) || null;
		},
		async onFeaturedStateChange (value: boolean, type: 'featured' | 'isFeaturedNodeDef') {

			try {
				this.settingFeaturedState = true;
				await db.runTransaction(async (transaction) => {
					const currentState = this._get(this.nodeRun, `results[${+this.index}].${type}`);

					if (currentState && !value) { // turning off
						const noOtherResultsAreFeatured = !this.nodeRun.results.find((result: RunnitNodeRunResult, index: number) => index !== +this.index && result[type]);
						const updatedResults = this.nodeRun.results.map((result: RunnitNodeRunResult, index: number) => {
							if (index === +this.index) {
								return _omit(result, [type]);
							}
							return result;
						});

						transaction.update(
							db.doc(`runnitNodeRuns/${this.nodeRun.id}`),
							{
								...(noOtherResultsAreFeatured && type === 'featured' && { featured: FieldValue.delete() }),
								...(noOtherResultsAreFeatured && type === 'isFeaturedNodeDef' && { isFeaturedNodeDef: FieldValue.delete() }),
								results: updatedResults
							},
						);

						// Update local state
						if (noOtherResultsAreFeatured && type === 'featured') {
							delete this.nodeRun.featured;
						}
						if (noOtherResultsAreFeatured && type === 'isFeaturedNodeDef') {
							delete this.nodeRun.isFeaturedNodeDef;
						}
						this.nodeRun.results = updatedResults;
					} else if (!currentState && value) { // turning on
						const updatedResults = this.nodeRun.results.map((result: RunnitNodeRunResult, index: number) => {
							if (index === +this.index) {
								return { ...result, [type]: true };
							}
							return result;
						});

						transaction.update(
							db.doc(`runnitNodeRuns/${this.nodeRun.id}`),
							{
								...(type === 'featured' && { featured: true }),
								...(type === 'isFeaturedNodeDef' && { isFeaturedNodeDef: true }),
								results: updatedResults
							}
						);

						// Update local state
						this.nodeRun.results = updatedResults;
						if (type === 'featured') {
							this.nodeRun.featured = true;
						}
						if (type === 'isFeaturedNodeDef') {
							this.nodeRun.isFeaturedNodeDef = true;
						}
					}
				});

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Successfully updated featured status!`,
					show: true,
				});

				// Sync to CMS if isFeaturedNodeDef changed
				if (type === 'isFeaturedNodeDef' && this.nodeRun.nodeDefId) {
					try {
						const syncNodeDefToCms = functions.httpsCallable('syncNodeDefToCms');
						await syncNodeDefToCms({ nodeDefId: this.nodeRun.nodeDefId });
						// Don't show error to user if sync fails - it's a background operation
					} catch (error) {
						console.error('Error syncing to CMS after featured status change:', error);
					}
				}
			} catch (error) {
				console.error('Error changing featured state', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Failed to update featured status`,
					show: true,
				});
			} finally {
				this.settingFeaturedState = false;
			}
		},
	},
	components: {
		ImageInfoFields,
	},
});
